import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  getLocalizationQuality,
  LocalizationCoverageResponse,
} from '@/services/localizationService';
import type { ReportFiltersState } from '@/features/reports/components/filters/ReportFilters';
import { createLogger } from '@/lib/logger';

// Create component-specific logger
const localizationQualityLogger = createLogger('useLocalizationQuality');

export type UseLocalizationQualityResult = UseQueryResult<LocalizationCoverageResponse>;

/**
 * Hook for fetching localization quality data with React Query
 *
 * @param filters - Filter criteria for the localization quality data
 * @returns The query result with localization quality data
 */
export function useLocalizationQuality(filters: ReportFiltersState): UseLocalizationQualityResult {
  return useQuery({
    queryKey: ['localizationQuality', filters],
    queryFn: async () => {
      localizationQualityLogger.info('Fetching localization quality data', {
        filters,
      });
      try {
        const data = await getLocalizationQuality(filters);
        localizationQualityLogger.info('Localization quality data fetched successfully', {
          localeCount: data.locale_stats.length,
          overallPercentage: data.overall.translated_pct,
        });
        return data;
      } catch (error) {
        localizationQualityLogger.error(
          'Failed to fetch localization quality data',
          {
            filters,
            error: (error as Error).message,
          },
          error as Error
        );
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    enabled: true,
  });
}

export default useLocalizationQuality;
